import React, { useMemo } from 'react';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import {
  useYesNoDataGridFilter,
  useOptionsDataGridFilter,
} from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useUniqueOptionsDataGridFilter } from '../../../../common/DataGrid/filters/useUniqueOptionsDataGridFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { Tag } from 'antd';
import { RESERVATION_STATUS } from '../../../../../utils/constants/enums.js';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';

const ReservationsTable = ({
  reservations,
  customSettings,
  isRoomReservationsAdmin,
  fromApproval: _fromApproval,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Build unique options filter for room names
  const roomNameFilterProps = useUniqueOptionsDataGridFilter(
    'room_name',
    reservations,
    {
      labelFormatter: (val, row) =>
        `${val} (${row?.department_code ?? ''})`.trim(),
      width: 200,
    },
  );
  // Build unique options filter for department (Pracovisko)
  const departmentFilterProps = useUniqueOptionsDataGridFilter(
    'department_code',
    reservations,
    {
      labelFormatter: (val, row) => {
        const code = val ?? '';
        const name = row?.department_name ?? '';
        return name ? `${name} (${code})` : String(code);
      },
      width: 200,
    },
  );
  const fromDateFilterProps = useDataGridDateRangeFilter('date_from');
  const toDateFilterProps = useDataGridDateRangeFilter('date_to');
  const fullNameFilterProps = useDataGridTextFilter('full_name');
  const isReservationSequenceFilterProps = useYesNoDataGridFilter(
    'rr_reservation_sequence_id',
  );
  const reasonFilterProps = useDataGridTextFilter('reason');
  const stateFilterProps = useOptionsDataGridFilter('state', {
    [RESERVATION_STATUS.ACCEPTED.STATE]: t(RESERVATION_STATUS.ACCEPTED.LABEL),
    [RESERVATION_STATUS.WAITING.STATE]: t(RESERVATION_STATUS.WAITING.LABEL),
    [RESERVATION_STATUS.REJECTED.STATE]: t(RESERVATION_STATUS.REJECTED.LABEL),
  });

  const columns = useMemo(
    () => [
      {
        title: t('Miestnosť'),
        dataIndex: 'room_name',
        sorter: dataGridTextSorter('room_name'),
        render: (room_name, row) => `${room_name} (${row.department_code})`,
        ...roomNameFilterProps,
      },

      {
        title: t('Rezervované pre'),
        dataIndex: 'full_name',
        sorter: dataGridTextSorter('full_name'),
        ...fullNameFilterProps,
      },

      {
        title: t('Od'),
        dataIndex: 'date_from',
        defaultSortOrder: 'descend',
        sorter: dataGridDateSorter('date_from'),
        render: (from) =>
          from ? dayjs(new Date(from)).format(DATETIME_FORMAT_DATETIME) : '-',
        ...fromDateFilterProps,
      },

      {
        title: t('Do'),
        dataIndex: 'date_to',
        sorter: dataGridDateSorter('date_to'),
        render: (to) =>
          to ? dayjs(new Date(to)).format(DATETIME_FORMAT_DATETIME) : '-',
        ...toDateFilterProps,
      },

      {
        title: t('Opakovaná'),
        dataIndex: 'hasReservationSequence',
        render: (val) => (val ? t('Áno') : t('Nie')),
        ...isReservationSequenceFilterProps,
      },

      {
        title: t('Dôvod rezervácie'),
        dataIndex: 'reason',
        ...reasonFilterProps,
      },

      {
        title: t('Stav'),
        dataIndex: 'state',
        sorter: dataGridTextSorter('state'),
        render: (state) => {
          switch (state) {
            case RESERVATION_STATUS.ACCEPTED.STATE:
              return (
                <Tag color={RESERVATION_STATUS.ACCEPTED.COLOR}>
                  {t(RESERVATION_STATUS.ACCEPTED.LABEL)}
                </Tag>
              );
            case RESERVATION_STATUS.WAITING.STATE:
              return (
                <Tag color={RESERVATION_STATUS.WAITING.COLOR}>
                  {t(RESERVATION_STATUS.WAITING.LABEL)}
                </Tag>
              );
            case RESERVATION_STATUS.REJECTED.STATE:
              return (
                <Tag color={RESERVATION_STATUS.REJECTED.COLOR}>
                  {t(RESERVATION_STATUS.REJECTED.LABEL)}
                </Tag>
              );
            default:
              return <Tag color="default">{state ? state : '?'}</Tag>;
          }
        },
        ...stateFilterProps,
      },
    ],
    [
      roomNameFilterProps,
      departmentFilterProps,
      fromDateFilterProps,
      toDateFilterProps,
      fullNameFilterProps,
      isReservationSequenceFilterProps,
      reasonFilterProps,
      stateFilterProps,
      t,
    ],
  );

  // Convert rr_reservation_sequence_id to boolean for filter compatibility and mark child rows
  const reservationsWithSequenceFlag = useMemo(() => {
    const transform = (row, isChild = false) => {
      const decorated = {
        ...row,
        hasReservationSequence: row.rr_reservation_sequence_id ? 1 : 0,
        isChild,
      };
      if (Array.isArray(row?.children) && row.children.length) {
        decorated.children = row.children.map((child) =>
          transform(child, true),
        );
      }
      return decorated;
    };

    return (reservations || []).map((r) => transform(r, false));
  }, [reservations]);

  const handleRowClick = (row) => {
    if (isRoomReservationsAdmin) {
      navigate(`${routes.cmsReservationsEdit}/${row.id}`);
    }
  };

  return (
    <DataGrid
      rowKey={(row) => row.id}
      dataSource={reservationsWithSequenceFlag}
      columns={columns}
      permissionsBasic={'reservations'}
      uniqueTableId={'reservations-table'}
      onRowClick={handleRowClick}
      customSettings={customSettings}
      rowClassName={(hasPermission) =>
        hasPermission ? 'reservations-withoutPermission' : ''
      }
    />
  );
};

ReservationsTable.propTypes = {
  reservations: PropTypes.array,
  customSettings: PropTypes.func,
  user: PropTypes.object,
  isAdmin: PropTypes.any,
  fromApproval: PropTypes.any,
};

export default ReservationsTable;
